import { useState } from "react";
import { Pencil, Trash2, Plus, Check, X, Package, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface LineItem {
  id: number;
  name: string;
  sku: string;
  quantity: number;
  price: string;
  total: string;
  image?: { src: string };
}

interface OrderItemsProps {
  items: LineItem[];
  currency: string;
  subtotal: string;
  shippingTotal: string;
  total: string;
  canEdit: boolean;
  onUpdateItem: (itemId: number, quantity: number) => Promise<void>;
  onRemoveItem: (itemId: number) => Promise<void>;
  onAddItem: () => void;
}

export function OrderItems({
  items,
  currency,
  subtotal,
  shippingTotal,
  total,
  canEdit,
  onUpdateItem,
  onRemoveItem,
  onAddItem,
}: OrderItemsProps) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQty, setEditQty] = useState<number>(1);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  function startEdit(item: LineItem) {
    setEditingId(item.id);
    setEditQty(item.quantity);
  }

  function cancelEdit() {
    setEditingId(null);
  }

  async function saveEdit(itemId: number) {
    setSaving(true);
    try {
      await onUpdateItem(itemId, editQty);
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(itemId: number) {
    setRemovingId(itemId);
    try {
      await onRemoveItem(itemId);
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className="ring-1 ring-foreground/10">
      <div className="flex items-center justify-between px-3 py-2.5 border-b">
        <span className="text-xs font-medium">Stavke narudžbe</span>
        {canEdit && (
          <Button variant="outline" size="xs" onClick={onAddItem}>
            <Plus className="h-3 w-3" />
            Dodaj
          </Button>
        )}
      </div>

      <div className="divide-y">
        {items?.map((item) => (
          <div key={item.id} className="flex gap-3 p-3 hover:bg-muted/30 transition-colors">
            <div className="h-12 w-12 shrink-0 bg-muted ring-1 ring-foreground/5 overflow-hidden">
              {item.image?.src ? (
                <img
                  src={item.image.src}
                  alt={item.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                  <Package className="h-4 w-4" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{item.name}</p>
                  {item.sku && (
                    <p className="text-xs text-muted-foreground mt-0.5">{item.sku}</p>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-0.5 shrink-0">
                    {editingId !== item.id && (
                      <Button variant="ghost" size="icon-xs" onClick={() => startEdit(item)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleRemove(item.id)}
                      disabled={removingId === item.id}
                    >
                      {removingId === item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mt-1.5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {editingId === item.id ? (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min={1}
                        className="h-6 w-14 text-xs text-center p-0.5"
                        value={editQty}
                        onChange={(e) => setEditQty(Number(e.target.value))}
                      />
                      <Button variant="ghost" size="icon-xs" onClick={() => saveEdit(item.id)} disabled={saving}>
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      </Button>
                      <Button variant="ghost" size="icon-xs" onClick={cancelEdit} disabled={saving}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <span>{item.quantity} × {parseFloat(item.price || "0").toFixed(2)} {currency}</span>
                  )}
                </div>
                <span className="text-xs font-medium">{parseFloat(item.total || "0").toFixed(2)} {currency}</span>
              </div>
            </div>
          </div>
        ))}

        {(!items || items.length === 0) && (
          <div className="px-3 py-8 text-center text-xs text-muted-foreground">
            Nema stavki
          </div>
        )}
      </div>

      <div className="border-t bg-muted/20">
        <div className="flex items-center justify-between px-3 py-1.5 text-xs text-muted-foreground">
          <span>Međuzbir</span>
          <span>{(items?.reduce((sum, item) => sum + parseFloat(item.total || "0"), 0) ?? 0).toFixed(2)} {currency}</span>
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 text-xs text-muted-foreground">
          <span>Dostava</span>
          <span>{parseFloat(shippingTotal || "0").toFixed(2)} {currency}</span>
        </div>
        <div className="flex items-center justify-between px-3 py-2 text-xs font-medium border-t">
          <span>Ukupno</span>
          <span>{parseFloat(total || "0").toFixed(2)} {currency}</span>
        </div>
      </div>
    </div>
  );
}
